/**
 * Medical reference data — age-scaled baselines.
 *
 * The adult defaults in vitals.js / scenarios.js are wrong for an infant: a
 * 6-month-old at HR 72 is bradycardic and 500 mL tidal volumes would be a
 * disaster. Normal Baseline (and the NRP/PALS tab) use these to reset the
 * patient, alarm limits and ventilator to age-appropriate values.
 *
 * Sources: PALS 2020 normal vital-sign tables; APLS weight estimate
 * ((age + 4) × 2 kg, 1–10 y); lung-protective 6–8 mL/kg VT.
 */

/** Estimated weight in kg from age in years (APLS / Luscombe-Owens). */
export function estimatedWeightKg(ageYears) {
  if (ageYears == null) return 70;
  if (ageYears < 0.1) return 3.5;
  if (ageYears < 1) return 3.5 + ageYears * 6.5;          // ≈10 kg at 1 y
  if (ageYears <= 10) return (ageYears + 4) * 2;
  if (ageYears < 18) return Math.min(70, 28 + (ageYears - 10) * 5);
  return 70;
}

/** Age bands with normal vitals (PALS) and alarm limits. */
const BANDS = [
  { max: 1 / 12, HR: 130, RR: 40, Sys: 70, Dia: 42, hrLow: 90, hrHigh: 180, rrLow: 25, rrHigh: 60, sysLow: 60 },
  { max: 1,      HR: 120, RR: 30, Sys: 85, Dia: 50, hrLow: 90, hrHigh: 170, rrLow: 20, rrHigh: 50, sysLow: 70 },
  { max: 3,      HR: 110, RR: 25, Sys: 92, Dia: 55, hrLow: 80, hrHigh: 150, rrLow: 15, rrHigh: 40, sysLow: 75 },
  { max: 6,      HR: 100, RR: 22, Sys: 95, Dia: 58, hrLow: 70, hrHigh: 140, rrLow: 15, rrHigh: 35, sysLow: 80 },
  { max: 12,     HR: 90,  RR: 18, Sys: 105, Dia: 65, hrLow: 60, hrHigh: 130, rrLow: 12, rrHigh: 30, sysLow: 85 },
  { max: 18,     HR: 80,  RR: 16, Sys: 115, Dia: 72, hrLow: 50, hrHigh: 120, rrLow: 10, rrHigh: 30, sysLow: 90 },
];
export function ageBand(ageYears) {
  if (ageYears == null) return null;
  return BANDS.find((b) => ageYears < b.max) ?? null;   // null = adult
}

/** Baseline vitals for Normal Baseline at this age (adult → null, use the scenario). */
export function baselineVitalsForAge(ageYears) {
  const b = ageBand(ageYears);
  if (!b) return null;
  return { HR: b.HR, RR: b.RR, NIBP_Sys: b.Sys, NIBP_Dia: b.Dia, ABP_Sys: b.Sys - 2, ABP_Dia: b.Dia - 3 };
}

/** Alarm limits for this age (adult → null, keep the instructor's limits). */
export function alarmLimitsForAge(ageYears) {
  const b = ageBand(ageYears);
  if (!b) return null;
  return {
    HR: { alarmLow: b.hrLow, alarmHigh: b.hrHigh },
    RR: { alarmLow: b.rrLow, alarmHigh: b.rrHigh },
    NIBP_Sys: { alarmLow: b.sysLow, alarmHigh: b.Sys + 40 },
    ABP_Sys: { alarmLow: b.sysLow, alarmHigh: b.Sys + 40 },
  };
}

/** Ventilator baseline for this age: 7 mL/kg VT, age-appropriate rate, C ≈ 1 mL/cmH₂O/kg. */
export function baselineVentForAge(ageYears) {
  const kg = estimatedWeightKg(ageYears);
  const b = ageBand(ageYears);
  const vte = Math.round(Math.min(500, 7 * kg));
  const rr = b ? Math.min(30, Math.max(12, Math.round(b.RR * 0.8))) : 12;
  const peep = 5;
  const compliance = Math.max(3, Math.round(kg * 1.0));          // ≈ 50 for an adult at 500 mL / 10 cmH₂O
  const pplat = Math.round(peep + vte / compliance);
  const ppeak = pplat + (b ? 4 : 3);
  return { vte, rr, peep, pplat, ppeak };
}
